import React from 'react'

import { CategoryCard, MediaQueries, ThemeProps } from '@hhgtech/hhg-components'
import {
  SectionContainerStyled,
  StyledContainer,
} from 'components/atoms/container/index.styled'
import FormatInnerHTML from 'components/atoms/FormatInnterHTML'
import { ProgressScrollList } from 'components/molecules/progressScrollList'
import {
  StyledCategoriesContainer,
  StyledCategoryWrapper,
} from 'components/pages/categories/index.styled'
import { IDescriptionCategory } from 'interfaces/categories.interface'
import WarningIcon from 'public/svg/warning.svg'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const NotifySponsoredStyled = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: #fff9eb;
  border-radius: 12px;
  font-size: 12px;
  vertical-align: middle;
  svg {
    margin-right: 8px;
  }
`

const SponsoredByStyled = styled.div`
  display: flex;
  color: ${(props: ThemeProps) => props.theme.mbColors.midGray};
  span {
    margin-right: 8px;
  }
`

const DescriptionBanner = styled.div`
  z-index: 1;
  padding-top: 24px;
  text-align: center;
  img {
    width: auto;
    max-width: ${(props: ThemeProps & { 'data-max-width'?: number | string }) =>
      props['data-max-width']}px;
    height: auto;
    max-height: ${(
      props: ThemeProps & { 'data-max-height'?: number | string },
    ) => props['data-max-height']}px;
    object-fit: contain;
  }
`

const DescriptionContainerStyled = styled(StyledContainer)`
  padding-top: 24px;
  padding-bottom: 24px;
  background: ${(props: ThemeProps & { 'data-bg'?: string }) =>
    props['data-bg'] || props.theme.mbColors.lightBlue};
  .sub-categories-container {
    width: max-content;
    flex-wrap: nowrap;
    padding: initial;
    ${MediaQueries.mbDown} {
      .category {
        width: 103px;
      }
    }
  }

  .des-content {
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .des-categories .scroll-track {
    padding-bottom: 5px;
  }

  .des-author {
    display: flex;
    flex: 1;
    font-size: 10px;
    .author-info {
      padding-left: 8px;
      line-height: 16px;
      .created-at {
        color: #9495a0;
      }
    }
  }
`

const ImgStyled = styled.img`
  object-fit: contain;
  object-position: contain;
`

const Description = (props: IDescriptionCategory) => {
  const { formatMessage: f } = useIntl()
  const {
    subCategories,
    header,
    author,
    description,
    banner,
    sponsored,
    imageColor,
    width,
    height,
  } = props
  return (
    <DescriptionContainerStyled
      as="article"
      className="des container-section"
      data-bg={imageColor}
    >
      {!!sponsored && (
        <SectionContainerStyled>
          <NotifySponsoredStyled>
            <WarningIcon />
            {f({ id: 'articlePage.sponsoredContent' })}
          </NotifySponsoredStyled>
        </SectionContainerStyled>
      )}
      <SectionContainerStyled as="h1" className="des-header">
        {header}
      </SectionContainerStyled>
      {!!sponsored && (
        <SectionContainerStyled>
          <SponsoredByStyled>
            <span>{f({ id: 'articlePage.sponsoredBy' })}</span>
            <ImgStyled
              style={{ width: 24, height: 24 }}
              width={24}
              height={24}
              src={sponsored.logo || '/images/bookDoctor.svg'}
              alt={'avatar'}
            />
          </SponsoredByStyled>
        </SectionContainerStyled>
      )}
      {!!author && (
        <SectionContainerStyled className="des-author" as="section">
          <div className="author-avatar">
            <ImgStyled
              width={32}
              height={32}
              src={author.avatar || '/images/bookDoctor.svg'}
              alt={'avatar'}
              style={{ width: 32, height: 32 }}
            />
          </div>
          <div className="author-info">
            <p className="major">
              {f({ id: 'categoryPage.info.reviewed' })}
              <strong>{` ${author.name} `}</strong>
            </p>
            <p className="created-at">{`on ${author.createdAt}`}</p>
          </div>
        </SectionContainerStyled>
      )}
      <SectionContainerStyled className="des-content" as="article">
        <FormatInnerHTML as="span" template={description} />
        {!!banner && (
          <DescriptionBanner
            className="des-banner"
            data-max-width={width || 180}
            data-max-height={height || 150}
          >
            <img
              loading="lazy"
              src={banner || '/images/bookDoctor.svg'}
              alt={'des-banner-img'}
            />
          </DescriptionBanner>
        )}
      </SectionContainerStyled>
      {!!subCategories && (
        <SectionContainerStyled as="section" className="des-categories">
          <ProgressScrollList numberOfDots={0} className="trust-list">
            <StyledCategoriesContainer
              data-layout-type="short"
              className="sub-categories-container"
            >
              {subCategories.map((category) => (
                <StyledCategoryWrapper
                  className="category"
                  data-layout-type="short"
                  key={String(category.termId)}
                >
                  <CategoryCard
                    category={category}
                    href={category.permalink}
                    imgBanner={
                      <img
                        loading="lazy"
                        width={63}
                        height={63}
                        src={category.iconUrl || '/images/bookDoctor.svg'}
                        alt={category.displayName}
                      />
                    }
                    label={category.displayName}
                  />
                </StyledCategoryWrapper>
              ))}
            </StyledCategoriesContainer>
          </ProgressScrollList>
        </SectionContainerStyled>
      )}
    </DescriptionContainerStyled>
  )
}

const DescriptionContainerStyledPC = styled(StyledContainer)`
  display: flex;
  align-items: center;
  padding-bottom: 24px;

  .info-container {
    z-index: 9;
    width: 70%;
    & > * {
      padding-top: 12px;
      padding-bottom: 12px;
    }
  }

  .sub-categories-container {
    width: max-content;
    flex-wrap: nowrap;
    padding: initial;
    .category {
      width: 103px;
    }
  }

  .des-banner {
    width: 30%;
    padding-top: 24px;
    text-align: center;
    img {
      object-fit: contain;
    }
  }
  .des-content {
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .des-author {
    display: flex;
    flex: 1;
    font-size: 10px;
    .author-info {
      padding-left: 8px;
      line-height: 16px;
      .created-at {
        color: #9495a0;
      }
    }
  }
`

const DescriptionSectionContainerStyled = styled(SectionContainerStyled)`
  position: relative;
  background: ${(props: ThemeProps & { 'data-bg'?: string }) =>
    props['data-bg'] || props.theme.mbColors.lightBlue};

  &::before {
    position: absolute;
    width: 100%;
    height: 120%;
    background: ${(props: ThemeProps & { 'data-bg'?: string }) =>
      props['data-bg'] || props.theme.mbColors.lightBlue};
    border-radius: 2150px 2150px 0 0 / 100px 100px 0 0;
    content: '';
    transform: rotate(180deg);
  }
`

export const DescriptionPC = (props: IDescriptionCategory) => {
  const { formatMessage: f } = useIntl()
  const {
    header,
    author,
    description,
    banner,
    sponsored,
    imageColor,
    width,
    height,
  } = props
  return (
    <DescriptionSectionContainerStyled
      data-bg={imageColor}
      className="container-des"
    >
      <DescriptionContainerStyledPC
        as="article"
        className="des container-section"
      >
        <div className="info-container">
          {!!sponsored && (
            <SectionContainerStyled>
              <NotifySponsoredStyled>
                <WarningIcon />
                {f({ id: 'articlePage.sponsoredContent' })}
              </NotifySponsoredStyled>
            </SectionContainerStyled>
          )}
          <SectionContainerStyled as="h1" className="des-header">
            {header}
          </SectionContainerStyled>
          {!!sponsored && (
            <SectionContainerStyled>
              <SponsoredByStyled>
                <span>{f({ id: 'articlePage.sponsoredBy' })}</span>
                <ImgStyled
                  style={{ width: 24, height: 24 }}
                  width={24}
                  height={24}
                  src={sponsored.logo || '/images/bookDoctor.svg'}
                  alt={'avatar'}
                />
              </SponsoredByStyled>
            </SectionContainerStyled>
          )}
          {!!author && (
            <SectionContainerStyled className="des-author" as="section">
              <div className="author-avatar">
                <ImgStyled
                  style={{ width: 24, height: 24 }}
                  width={32}
                  height={32}
                  src={author.avatar || '/images/bookDoctor.svg'}
                  alt={'avatar'}
                />
              </div>
              <div className="author-info">
                <p className="major">
                  {f({ id: 'categoryPage.info.reviewed' })}
                  <strong>{` ${author.name} `}</strong>
                </p>
                <p className="created-at">{`on ${author.createdAt}`}</p>
              </div>
            </SectionContainerStyled>
          )}
          <SectionContainerStyled className="des-content" as="article">
            <FormatInnerHTML as="span" template={description} />
          </SectionContainerStyled>
        </div>
        <DescriptionBanner
          as="aside"
          className="des-banner"
          data-max-width={width || 180}
          data-max-height={height || 150}
        >
          <img
            loading="lazy"
            src={banner || '/images/bookDoctor.svg'}
            alt={'des-banner-img'}
          />
        </DescriptionBanner>
      </DescriptionContainerStyledPC>
    </DescriptionSectionContainerStyled>
  )
}

export default Description
